import * as React from "react";
import { UploadContext, type UploadContextType } from "./upload-provider";
import {
  isJunkFilename,
  useFileUpload,
  type UploadOptions,
  type AggregatedResult,
  type UseFileUploadOptions,
} from "@/hooks/useFileUpload";
import { UploadProgressDialog } from "@/components/upload-progress-dialog";
import { useToast } from "@/components/ui/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateUploadQueries } from "./upload-query-invalidation";

interface UploadProviderProps {
  children: React.ReactNode;
  options?: UseFileUploadOptions;
}

export function UploadProvider({ children, options = {} }: UploadProviderProps) {
  const fileUpload = useFileUpload(options);
  const [files, setFiles] = React.useState<File[]>([]);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [isMinimized, setIsMinimized] = React.useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Track upload status
  const isUploading = fileUpload.state.status === "uploading";
  const isDone =
    fileUpload.state.status === "completed" ||
    fileUpload.state.status === "cancelled" ||
    fileUpload.state.status === "error";

  // Track previous status to detect completion (single source of truth for toasts)
  const prevStatus = React.useRef(fileUpload.state.status);
  React.useEffect(() => {
    const currentStatus = fileUpload.state.status;
    const wasUploading = prevStatus.current === "uploading";
    prevStatus.current = currentStatus;

    if (!wasUploading) return;

    if (currentStatus === "completed") {
      const totalUploaded = fileUpload.state.completedFiles;
      toast({
        title: `${totalUploaded} document${totalUploaded !== 1 ? "s" : ""} uploaded`,
        description: "Upload completed successfully",
      });
    } else if (currentStatus === "error" && fileUpload.state.error) {
      toast({
        title: "Upload Error",
        description: fileUpload.state.error,
        variant: "destructive",
      });
    }
  }, [fileUpload.state.status, fileUpload.state.completedFiles, fileUpload.state.error, toast]);

  // Wrapper around upload to track files and manage dialog
  const upload = React.useCallback(
    async (
      uploadFiles: File[],
      uploadOptions: UploadOptions,
    ): Promise<AggregatedResult> => {
      // Block new uploads while one is in progress
      if (fileUpload.state.status === "uploading") {
        toast({
          title: "Upload Blocked",
          description: "An upload is already in progress. Please wait for it to complete.",
          variant: "destructive",
        });
        return {
          success: false,
          totalUploaded: 0,
          totalIgnored: 0,
          autoCategorizedDocs: [],
          ignoredDocs: [],
          batchResults: [],
          errors: ["An upload is already in progress. Please wait for it to complete."],
        };
      }

      // Keep the dialog's file list in sync with what actually uploads:
      // the hook drops OS/Office junk (~$ locks, .DS_Store) up front.
      const realFiles = uploadFiles.filter((f) => !isJunkFilename(f.name));
      setFiles(realFiles);
      // Show dialog for 2+ files
      if (realFiles.length >= 2) {
        setIsDialogOpen(true);
        setIsMinimized(false);
      }
      const result = await fileUpload.upload(realFiles, uploadOptions);
      if (result.totalUploaded > 0) {
        invalidateUploadQueries(queryClient, uploadOptions);
      }
      return result;
    },
    [fileUpload, queryClient, toast],
  );

  const reset = React.useCallback(() => {
    fileUpload.reset();
    setFiles([]);
    setIsDialogOpen(false);
    setIsMinimized(false);
  }, [fileUpload]);

  const closeDialog = React.useCallback(() => {
    setIsDialogOpen(false);
    setIsMinimized(false);
    // Reset state when closing after completion
    if (isDone) {
      fileUpload.reset();
      setFiles([]);
    }
  }, [isDone, fileUpload]);

  const contextValue: UploadContextType = React.useMemo(
    () => ({
      state: fileUpload.state,
      files,
      isDialogOpen,
      isUploading,
      upload,
      cancel: fileUpload.cancel,
      reset,
      openDialog: () => setIsDialogOpen(true),
      closeDialog,
      minimizeDialog: () => setIsMinimized(true),
      expandDialog: () => setIsMinimized(false),
      isMinimized,
    }),
    [
      fileUpload.state,
      fileUpload.cancel,
      files,
      isDialogOpen,
      isUploading,
      isMinimized,
      upload,
      reset,
      closeDialog,
    ],
  );

  return (
    <UploadContext.Provider value={contextValue}>
      {children}
      {/* Global upload progress dialog - renders outside of page components */}
      <UploadProgressDialog
        state={fileUpload.state}
        files={files}
        onCancel={fileUpload.cancel}
        onClose={closeDialog}
        isOpen={isDialogOpen}
        isMinimized={isMinimized}
        onMinimize={() => setIsMinimized(true)}
        onExpand={() => setIsMinimized(false)}
      />
    </UploadContext.Provider>
  );
}
